import React, { useEffect, useMemo, useState } from 'react'
import { Card, EmptyState, PageTitle, SearchField, StatusBadge, formatDate } from '../components/Ui'
import { BackLink, DetailGrid, SummaryGrid } from '../components/AdminCommon'
import AdminFormModal, { FormField, FormGrid } from '../components/AdminFormModal'
import AdminHtmlEditor from '../components/AdminHtmlEditor'
import AdminHtmlPreview from '../components/AdminHtmlPreview'
import { adminRequest } from '../api/client'

const PUBLIC_APP_ORIGIN = String(import.meta.env.VITE_PUBLIC_APP_ORIGIN || 'https://difan.co.kr').replace(/\/$/, '')

function localDateTime(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (number) => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
function iso(value) { return value ? new Date(value).toISOString() : null }
function displayTitle(item) { return item?.title || item?.policyKey || '정책·안내 페이지' }
function nextVersionCode(versions) {
  const numbers = (versions || []).map((row) => Number(String(row.versionCode || '').match(/\d+/)?.[0] || 0)).filter(Boolean)
  return `v${Math.max(0, ...numbers) + 1}`
}
function currentVersion(versions) {
  const now = Date.now()
  return (versions || []).filter((row) => row.status === 'PUBLISHED' && new Date(row.effectiveAt).getTime() <= now)
    .sort((a,b) => new Date(b.effectiveAt).getTime() - new Date(a.effectiveAt).getTime())[0] || null
}
function editorFrom(version = null, versions = []) {
  if (version) return {
    versionId: version.versionId,
    version: version.version,
    versionCode: version.versionCode,
    title: version.title,
    summary: version.summary || '',
    bodyHtml: version.bodyHtml || '',
    status: version.status,
    effectiveAt: localDateTime(version.effectiveAt),
    isNew: false
  }
  return {
    versionId: null,
    version: null,
    versionCode: nextVersionCode(versions),
    title: currentVersion(versions)?.title || '',
    summary: currentVersion(versions)?.summary || '',
    bodyHtml: currentVersion(versions)?.bodyHtml || '<section><h2>제목</h2><p>내용을 입력하세요.</p></section>',
    status: 'DRAFT',
    effectiveAt: localDateTime(),
    isNew: true
  }
}

export function InfoPages({ navigate }) {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let disposed = false
    setLoading(true)
    adminRequest('/api/v1/admin/policies')
      .then((response) => { if (!disposed) setItems(Array.isArray(response?.data) ? response.data : []) })
      .catch((nextError) => { if (!disposed) setError(nextError?.message || '정책·안내 페이지를 불러오지 못했습니다.') })
      .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [])

  const rows = useMemo(() => items.filter((item) => !search || [item.title,item.policyKey,item.slug,item.owner,item.route,item.summary,item.consentType].join(' ').toLowerCase().includes(search.toLowerCase())), [items, search])

  return <>
    <PageTitle eyebrow="POLICY / INFORMATION" title="정책·안내 페이지" description="푸터 안내 페이지와 회원가입 약관의 발행 버전을 한곳에서 관리합니다." />
    <div className="policy-callout"><strong>버전별로 조회·수정할 수 있습니다.</strong><span>기존 버전을 선택해 내용을 정정할 수 있으며, 버전 코드는 유지됩니다. 큰 정책 변경은 새 버전 발행을 권장하고 모든 수정 전·후 내용은 관리자 감사 이력에 남습니다.</span></div>
    <Card className="table-card">
      <div className="table-toolbar"><SearchField value={search} onChange={setSearch} placeholder="페이지명, 약관 종류, 담당, 경로 검색" /></div>
      <div className="table-result-bar"><span>총 <strong>{rows.length}</strong>개 문서</span><small>푸터 페이지 + 회원가입 약관</small></div>
      {loading ? <div className="admin-empty-inline">불러오는 중입니다.</div> : error ? <EmptyState title="정책·안내 페이지 조회 실패" description={error} /> : rows.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>문서</th><th>사용 위치</th><th>현재 버전</th><th>담당</th><th>시행일</th><th>버전 이력</th></tr></thead><tbody>{rows.map((item) => <tr className="clickable-row" key={item.documentId} onClick={() => navigate(`/content/info-pages/${encodeURIComponent(item.policyKey)}`)}><td><strong>{displayTitle(item)}</strong><small>{item.policyKey}</small></td><td>{item.route ? <><span className="mono">{item.route}</span><small>푸터·공개 페이지</small></> : <><span>{item.consentType || '내부 정책'}</span><small>회원가입 약관</small></>}</td><td>{item.versionCode ? <><StatusBadge status="PUBLISHED">발행</StatusBadge><small>{item.versionCode}</small></> : <span>-</span>}</td><td>{item.owner}</td><td>{item.effectiveAt ? formatDate(item.effectiveAt, true) : '-'}</td><td>{item.versionCount || 0}개</td></tr>)}</tbody></table></div> : <EmptyState title="등록된 정책 문서가 없습니다." />}
    </Card>
  </>
}

export function InfoPageDetail({ id, navigate }) {
  const [document, setDocument] = useState(null)
  const [versions, setVersions] = useState([])
  const [editor, setEditor] = useState(null)
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true); setError('')
    try {
      const response = await adminRequest(`/api/v1/admin/policies/${encodeURIComponent(id)}`)
      setDocument(response?.data?.document || null)
      setVersions(Array.isArray(response?.data?.versions) ? response.data.versions : [])
    } catch (nextError) {
      setError(nextError?.message || '정책 문서를 불러오지 못했습니다.')
      setDocument(null); setVersions([])
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [id])

  const published = useMemo(() => currentVersion(versions), [versions])
  const drafts = useMemo(() => versions.filter((row) => row.status === 'DRAFT'), [versions])

  const save = async (event) => {
    event.preventDefault()
    if (saving || !editor) return
    if (!editor.versionCode.trim() || !editor.title.trim() || !editor.summary.trim() || !editor.bodyHtml.trim() || !editor.effectiveAt) {
      setNotice('버전, 제목, 요약, 본문, 시행일을 모두 입력해주세요.')
      return
    }
    if (editor.status === 'PUBLISHED') { const message=editor.isNew?`${editor.versionCode} 버전을 발행할까요?`:`${editor.versionCode} 발행본을 수정할까요? 현재 공개 내용이 즉시 바뀌며 수정 전·후 내용은 관리자 감사 이력에 보존됩니다.`; if(!window.confirm(message)) return }
    setSaving(true); setNotice('')
    const body = { versionCode:editor.versionCode.trim(), title:editor.title.trim(), summary:editor.summary.trim(), bodyHtml:editor.bodyHtml.trim(), status:editor.status, effectiveAt:iso(editor.effectiveAt) }
    try {
      if (editor.isNew) await adminRequest(`/api/v1/admin/policies/${encodeURIComponent(document.policyKey)}/versions`, { method:'POST', body })
      else await adminRequest(`/api/v1/admin/policies/${encodeURIComponent(document.policyKey)}/versions/${encodeURIComponent(editor.versionId)}`, { method:'PUT', headers:{'If-Match':String(editor.version)}, body })
      setEditor(null)
      setNotice(editor.status === 'PUBLISHED' ? `${editor.versionCode} 버전을 발행했습니다.` : `${editor.versionCode} 초안을 저장했습니다.`)
      await load()
    } catch (nextError) { setNotice(nextError?.message || '정책 버전 저장에 실패했습니다.') }
    finally { setSaving(false) }
  }

  if (loading) return <><BackLink onClick={() => navigate('/content/info-pages')} /><PageTitle eyebrow="POLICY PAGE" title="정책 문서" description="불러오는 중입니다." /><Card><div className="admin-empty-inline">불러오는 중입니다.</div></Card></>
  if (!document || error) return <><BackLink onClick={() => navigate('/content/info-pages')} /><PageTitle eyebrow="POLICY PAGE" title="정책 문서" description="문서를 확인할 수 없습니다." />{error && <div className="admin-notice"><strong>안내</strong><span>{error}</span></div>}</>

  return <>
    <BackLink onClick={() => navigate('/content/info-pages')} />
    <PageTitle eyebrow="POLICY PAGE DETAIL" title={published?.title || document.policyKey} description="현재 발행본과 과거 버전 이력을 관리합니다." actions={<>{document.route && <button type="button" className="button soft" onClick={() => window.open(`${PUBLIC_APP_ORIGIN}${document.route}`, '_blank', 'noopener,noreferrer')}>공개 미리보기</button>}<button type="button" className="button primary" onClick={() => setEditor(editorFrom(null, versions))}>새 버전 등록</button></>} />
    {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}
    <SummaryGrid items={[{ label:'현재 발행 버전', value:published?.versionCode || '없음' },{ label:'시행일', value:published?.effectiveAt ? formatDate(published.effectiveAt, true) : '-' },{ label:'사용 위치', value:document.route || document.consentType || '-' },{ label:'전체 버전', value:`${versions.length}개` }]} />

    <div className="dashboard-grid half">
      <Card title="문서 정보"><DetailGrid items={[{ label:'정책 키', value:document.policyKey },{ label:'Slug', value:document.slug },{ label:'담당', value:document.owner },{ label:'공개 경로', value:document.route || '회원가입 팝업 전용', wide:true },{ label:'동의 유형', value:document.consentType || '-', wide:true }]} /></Card>
      <Card title="버전 관리 원칙"><div className="admin-detail-stack"><div><span>기존 버전 수정</span><strong>가능</strong><p>선택한 버전의 제목·요약·본문·시행일을 정정할 수 있습니다. 버전 코드는 변경하지 않습니다.</p></div><div><span>변경 이력</span><strong>감사 로그 보존</strong><p>기존 버전을 수정하면 서버가 수정 전·후 내용을 관리자 감사 이력에 남깁니다. 큰 정책 변경은 새 버전 발행을 권장합니다.</p></div></div></Card>
    </div>

    {published && <Card title={`현재 발행본 · ${published.versionCode}`} description={`${formatDate(published.effectiveAt, true)} 시행`}><AdminHtmlPreview title={`${published.versionCode} 정책 본문 미리보기`} bodyHtml={published.bodyHtml} description="정책 HTML은 관리자 레이아웃과 분리된 영역에서 표시됩니다." /></Card>}

    <Card title="버전 이력" description="각 버전을 선택해 정정할 수 있습니다. 기존 버전의 버전 코드는 고정되며 수정 전·후 내용은 관리자 감사 이력에 남습니다.">
      {versions.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>버전</th><th>상태</th><th>제목</th><th>시행일</th><th>발행일</th><th>관리</th></tr></thead><tbody>{versions.map((row) => <tr key={row.versionId}><td><strong>{row.versionCode}</strong></td><td><StatusBadge status={row.status}>{row.status === 'PUBLISHED' ? '발행' : row.status === 'DRAFT' ? '초안' : '보관'}</StatusBadge></td><td>{row.title}</td><td>{formatDate(row.effectiveAt, true)}</td><td>{row.publishedAt ? formatDate(row.publishedAt, true) : '-'}</td><td><button type="button" className="text-button" onClick={() => setEditor(editorFrom(row, versions))}>{row.status === 'DRAFT' ? '초안 수정' : '버전 수정'}</button></td></tr>)}</tbody></table></div> : <EmptyState title="등록된 버전이 없습니다." />}
    </Card>

    <AdminFormModal open={Boolean(editor)} title={editor?.isNew ? '새 정책 버전 등록' : `${editor?.versionCode} 버전 수정`} description={editor?.isNew ? '새 버전을 만들거나 발행합니다.' : '선택한 버전을 정정합니다. 수정 전·후 내용은 관리자 감사 이력에 보존됩니다.'} onClose={() => !saving && setEditor(null)} onSubmit={save} submitLabel={saving ? '저장 중…' : editor?.status === 'PUBLISHED' ? '발행' : '초안 저장'} size="large">
      {editor && <FormGrid>
        <FormField label="버전 코드" required><input required disabled={!editor.isNew} value={editor.versionCode} onChange={(event) => setEditor({...editor,versionCode:event.target.value})} placeholder="예: v2" /></FormField>
        <FormField label="상태"><select disabled={!editor.isNew && editor.status === 'PUBLISHED'} value={editor.status} onChange={(event) => setEditor({...editor,status:event.target.value})}><option value="DRAFT">초안</option><option value="PUBLISHED">발행</option></select></FormField>
        <FormField label="시행 일시" required><input required type="datetime-local" value={editor.effectiveAt} onChange={(event) => setEditor({...editor,effectiveAt:event.target.value})} /></FormField>
        <FormField label="제목" required wide><input required value={editor.title} onChange={(event) => setEditor({...editor,title:event.target.value})} /></FormField>
        <FormField label="요약" required wide><textarea required value={editor.summary} onChange={(event) => setEditor({...editor,summary:event.target.value})} /></FormField>
        <FormField label="HTML 본문" required wide><AdminHtmlEditor required ariaLabel="정책 문서 본문" minHeight={380} value={editor.bodyHtml} onChange={(bodyHtml) => setEditor((current) => ({...current,bodyHtml}))} /></FormField>
      </FormGrid>}
    </AdminFormModal>
  </>
}
